import React, { FC, useEffect, useState } from 'react';

import { TemplateBasicFragment } from '../../../../generated/graphql';
import useToast from '../../../../hooks/useToast';
import translate from '../../../../i18n/translate';
import TopicTemplateSelection from '../../../MUI/Compose/TopicTemplateSelection';
import Popover, { ExternalPopoverProps } from '../../../MUI/Popover';

import { handleTemplateSelect } from '../../helpers';

import ComposeTemplateTrigger from './ComposeTemplateTrigger';

type Props = {
  ariaDescribedBy: string;
  ariaLabelledBy: string;
  dataTemplateAll: TemplateBasicFragment[];
  dataTemplateCurrent: TemplateBasicFragment['id'] | null;
  dataSearchValue: string;
  isDefaultDraftChanged?: boolean;
  isLoading: boolean;
  onSearch: (term: string) => void;
  requestTemplateChange: (templateId: TemplateBasicFragment['id']) => void;
  requestTemplateRemove: () => void;
  requestTemplateSave: () => void;
};

/**
 * Template selection for compose modal
 *
 * @param props                       Props passed to the component
 * @param props.ariaDescribedBy       Accessible form description
 * @param props.ariaLabelledBy        Accessible form title
 * @param props.dataTemplateAll       All available templates
 * @param props.dataTemplateCurrent   Currently selected template
 * @param props.dataSearchValue       <input type="search" /> value attribute
 * @param props.isDefaultDraftChanged Whether the default values of the draft have been changed
 * @param props.isLoading             Whether the data is still being loaded
 * @param props.onSearch              Callback to invoke when search term is changed
 * @param props.requestTemplateChange Request that selected template is changed
 * @param props.requestTemplateRemove Request that current template is set to null
 * @param props.requestTemplateSave   Request that the current template is saved
 *
 * @returns                           Component itself
 */
const ComposeModalTemplate: FC<Props> = ({
  ariaDescribedBy,
  ariaLabelledBy,
  dataTemplateAll,
  dataTemplateCurrent,
  dataSearchValue,
  isDefaultDraftChanged,
  isLoading,
  onSearch,
  requestTemplateChange,
  requestTemplateRemove,
  requestTemplateSave,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const pushToast = useToast();

  useEffect(() => {
    if (
      isDefaultDraftChanged &&
      anchorEl !== null &&
      dataTemplateAll.length > 0
    ) {
      pushToast({
        text: translate('TEMPLATE__SELECT_WARNING'),
        type: 'warning',
      });
    }
  }, [anchorEl, dataTemplateAll.length, isDefaultDraftChanged, pushToast]);

  /**
   * Closes the popover after selecting a template
   */
  const handleClose = () => {
    setAnchorEl(null);
  };

  /**
   * Select template or unselect if it's already selected
   *
   * @param template Template id for selection
   */
  const requestTemplateSelect = (template: TemplateBasicFragment['id']) => {
    handleTemplateSelect({
      currentTemplateId: dataTemplateCurrent,
      newTemplateId: template,
      onClose: handleClose,
      requestTemplateChange,
      requestTemplateRemove,
      requestTemplateSave,
    });
  };

  const popoverHandlers: ExternalPopoverProps = {
    anchorEl,
    onStateChange: setAnchorEl,
  };

  return (
    <Popover
      aria-disabled={isLoading}
      externalHandlerProps={popoverHandlers}
      shouldHaveOffset={true}
      trigger={<ComposeTemplateTrigger />}
    >
      <TopicTemplateSelection
        ariaDescribedBy={ariaDescribedBy}
        ariaLabelledBy={ariaLabelledBy}
        dataAll={dataTemplateAll}
        dataCurrent={dataTemplateCurrent}
        dataSearchValue={dataSearchValue}
        isLoading={isLoading}
        onSearch={onSearch}
        requestDataSelect={requestTemplateSelect}
        type="template"
      />
    </Popover>
  );
};

export default ComposeModalTemplate;
